// Components/Services/AddServices.jsx
import React, { useEffect, useRef, useState } from "react";
import { Card, Button, Form, Row, Col, Alert, Tabs, Tab } from "react-bootstrap";
import { AddServicesPracTeam } from "./AddServicesPracTeam";
import { AddServicesPricing } from "./AddServicesPricing";
import { AddServicesSettings } from "./AddServicesSettings";
import { FaUpload, FaTimes } from "react-icons/fa";
import { createService, getUsersByPracticeNumber, getPractice } from "../../api";
import { jwtDecode } from "jwt-decode";
import "./Services.css";

const initialServiceState = {
  servicesName: "",
  servicesDescription: "",
  servicesDuration: "",
  settings: {
    visible: true,
    websitePrice: "",
    bookable: false,
    upfrontPayable: false,
  },
  pricing: {
    flatRate: "",
    invoiceDescription: "",
    cashDiscountPercentage: "",
    upfrontDiscountPercentage: "",
    specialOfferPercentage: "",
    specialOfferExpiry: "",
    tariffCodeDescription: "",
    paymentOptions: {
      useFlatRate: false,
      useFlatAndMedicalAidRate: false,
    },
    selectedTariffs: [],
    selectedNappi: [],
  },
};

export function AddServiceInline({ onServiceUpdate = () => {}, onClose = () => {} }) {
  const successRef = useRef(null);

  const [pracName, setPracName] = useState([]);
  const [selectedPractices, setSelectedPractices] = useState([]);
  const [uniqueTeamMember, setUniqueTeamMember] = useState([]);
  const [selectedTeamMembers, setSelectedTeamMembers] = useState([]);
  const [successAlert, setSuccessAlert] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [errors, setErrors] = useState({});
  const [user, setUser] = useState({});
  const [validatePricingFunction, setValidatePricingFunction] = useState(() => () => true);
  const [pricingErrors, setPricingErrors] = useState({});

  const [service, setService] = useState(initialServiceState);
 


  // Load practices + team for the logged-in user
  useEffect(() => {
    async function loadData() {
      const token = sessionStorage.getItem("user");
      if (!token) return;

      const decoded = jwtDecode(token);
      setUser(decoded);

      const practices = await getPractice();
      const userPractices = practices.filter((p) => decoded.practice?.includes(p.practiceNumber));
      setPracName(userPractices);

      const teamLists = await Promise.all(
        userPractices.map((p) => getUsersByPracticeNumber(p.practiceNumber))
      );
      const flat = teamLists.flat();
      const dedup = Array.from(new Map(flat.map((m) => [m._id, m])).values());
      setUniqueTeamMember(dedup);
    }
    loadData();
  }, []);

  const validateForm = () => {
    const newErrors = {};
    if (!service.servicesName) newErrors.servicesName = "Service name is required.";
    if (!service.servicesDescription) newErrors.servicesDescription = "Service description is required.";
    if (!service.servicesDuration || !/^\d{2}:\d{2}$/.test(service.servicesDuration)) {
      newErrors.servicesDuration = "Enter duration in HH:mm format.";
    }
    if (!selectedPractices.length) newErrors.servicesPractice = "At least one practice must be selected.";
    if (!selectedTeamMembers.length) newErrors.servicesUser = "At least one team member must be selected.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");

    const isServiceValid = validateForm();
    const isPricingValid = typeof validatePricingFunction === 'function'
  ? validatePricingFunction()
  : true;
    if (!isServiceValid || !isPricingValid) return;

    try {
      const formattedDuration = service.servicesDuration.padStart(5, "0");

      const resp = await createService({
        ...service,
        servicesDuration: formattedDuration,
        servicesPractice: selectedPractices,
        servicesUser: selectedTeamMembers,
      });

      if (resp?.status === 200) {
        // refresh the list immediately
        onServiceUpdate();

        // small success flash + smooth scroll
        setSuccessAlert(true);
        requestAnimationFrame(() => {
          successRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        });

        setTimeout(() => {
          setSuccessAlert(false);
          // reset local state (optional since we close)
          setService(initialServiceState);
          setSelectedPractices([]);
          setSelectedTeamMembers([]);
          // close inline panel
          onClose();
        }, 700);
      }
    } catch (err) {
      console.error("Error creating service:", err);
      const msg =
        err?.response?.data?.message ||
        "Failed to create service. Please try again.";
      setSubmitError(msg);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "servicesDuration") {
      const input = value.replace(/[^0-9]/g, "");
      let formatted = input.length > 2 ? `${input.slice(0, 2)}:${input.slice(2, 4)}` : input;
      if (formatted.length > 5) formatted = formatted.slice(0, 5);
      setService((prev) => ({ ...prev, [name]: formatted }));
    } else {
      setService((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handlePracticeChange = (practiceNumber) => {
    setSelectedPractices((prev) =>
      prev.includes(practiceNumber)
        ? prev.filter((p) => p !== practiceNumber)
        : [...prev, practiceNumber]
    );
  };

  const handleTeamMemberChange = (userId) => {
    setSelectedTeamMembers((prev) =>
      prev.includes(userId) ? prev.filter((u) => u !== userId) : [...prev, userId]
    );
  };

  return (
    <Card className="shadow-sm border-0 mb-3">
      <Card.Body className="pt-2">
        {successAlert && (
          <Alert ref={successRef} variant="success" className="w-100 fs-6 fw-normal py-2">
            The service was added successfully!
          </Alert>
        )}

        {submitError && (
          <Alert
            variant="danger"
            className="w-100 fs-6 fw-normal py-2"
            dismissible
            onClose={() => setSubmitError("")}
          >
            {submitError}
          </Alert>
        )}

        <Form onSubmit={handleSubmit}>
          <h6 className="text-primary">Service Information</h6>

          <Row className="mb-3">
            <Form.Group as={Col} controlId="servicesName">
              <Form.Label>Service Name</Form.Label>
              <Form.Control
                name="servicesName"
                value={service.servicesName}
                isInvalid={!!errors.servicesName}
                onChange={handleChange}
              />
              <Form.Control.Feedback type="invalid">{errors.servicesName}</Form.Control.Feedback>
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} controlId="servicesDescription">
              <Form.Label>Service Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="servicesDescription"
                value={service.servicesDescription}
                isInvalid={!!errors.servicesDescription}
                onChange={handleChange}
              />
              <Form.Control.Feedback type="invalid">
                {errors.servicesDescription}
              </Form.Control.Feedback>
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Col md={2}>
              <Form.Group as={Col} controlId="servicesDuration">
                <Form.Label>Service Duration</Form.Label>
                <Form.Control
                  name="servicesDuration"
                  value={service.servicesDuration}
                  placeholder="00:00"
                  isInvalid={!!errors.servicesDuration}
                  onChange={handleChange}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.servicesDuration}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <hr />
          <h6 className="text-primary">Service Settings</h6>
          <p className="text-muted">Complete each of the tabs below to set up the service.</p>

          <Tabs defaultActiveKey="practice" id="service-tabs" className="custom-tabs" justify>
            <Tab
              eventKey="practice"
              title={
                <span style={(errors.servicesPractice || errors.servicesUser) ? { color: "var(--bs-danger)" } : {}}>
                  Practice &amp; Team
                </span>
              }
            >
              <div className="tab-content-bordered">
                <AddServicesPracTeam
                  pracName={pracName}
                  selectedPractices={selectedPractices}
                  uniqueTeamMember={uniqueTeamMember}
                  selectedTeamMembers={selectedTeamMembers}
                  handlePracticeChange={handlePracticeChange}
                  handleTeamMemberChange={handleTeamMemberChange}
                  errors={errors}
                />
              </div>
            </Tab>

            <Tab
              eventKey="billing"
              title={
                <span style={Object.keys(pricingErrors).length ? { color: "var(--bs-danger)" } : {}}>
                  Billing Settings
                </span>
              }
            >
              <div className="tab-content-bordered">
                <AddServicesPricing
                  onPricingChange={(p) => setService((prev) => ({ ...prev, pricing: p }))}
                  selectedPractices={selectedPractices}
                  pracName={pracName}
                  user={user}
                  onValidate={(validateFn) => setValidatePricingFunction(() => validateFn)}
                  onErrorsChange={(e) => setPricingErrors(e)}
                />
              </div>
            </Tab>

            <Tab eventKey="settings" title="General Settings">
              <div className="tab-content-bordered">
                <AddServicesSettings
                  onSettingsChange={(s) => setService((prev) => ({ ...prev, settings: s }))}
                />
              </div>
            </Tab>
          </Tabs>

          <div className="d-flex gap-2 mt-3">
            <Button variant="primary" className="px-5 py-2" type="submit">
              <FaUpload className="me-2" /> Submit
            </Button>
            <Button variant="outline-secondary" className="px-5 py-2" type="button" onClick={onClose}>
              <FaTimes className="me-2" /> Cancel
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
}

export default AddServiceInline;
